import { useEffect, useRef, useState } from 'react';
import { initials } from '../lib/format.js';

/**
 * The seat count, drawn rather than stated.
 *
 * "2 places" is ambiguous: two seats in the car, or two still free? So each seat
 * is a token in order, left to right: the driver, then the seats already taken,
 * then the seats still free, then any seat the driver kept back for gear.
 *
 * A token carries initials, short enough to fit and too short to identify
 * someone on their own. In the compact row the full name is one hover away on a
 * pointer and one tap away on a screen, which is what lets the opened card drop
 * the second copy of the same seats.
 *
 * compact  - the ride card header. A free seat is a button: pressing it is
 *            taking it.
 * detailed - every seat labelled, held seats included, plus the count in words.
 *            Used in the forms, where the point is to read the layout.
 * summary  - the arithmetic in words, no tokens.
 */

function Seat({ kind, name, label, color }) {
  const glyph = kind === 'free' ? '+' : kind === 'held' ? '×' : initials(name);
  return (
    <span className={`seat-token seat-${kind}`} style={color ? { '--seat-color': color } : undefined}>
      <span className="seat-glyph">{glyph}</span>
      {label && <span className="seat-label">{label}</span>}
    </span>
  );
}

export default function SeatStrip({
  seats,
  driverName = '',
  passengers = [],
  color,
  variant = 'compact',
  onSeatClick,
}) {
  const { totalSeats, seatsOffered, seatsTaken, seatsFree } = seats;
  const held = Math.max(0, totalSeats - 1 - seatsOffered);
  const detailed = variant === 'detailed';

  /* Which seat is showing its name because it was tapped. Hovering never comes
     through here: CSS handles that, so a pointer needs no click at all. */
  const [openSeat, setOpenSeat] = useState(null);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!openSeat) return undefined;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpenSeat(null);
    };
    window.addEventListener('pointerdown', close, true);
    return () => window.removeEventListener('pointerdown', close, true);
  }, [openSeat]);

  const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || '?';

  const summary =
    `${seatsFree === 0 ? 'Aucune place libre' : `${seatsFree} place${seatsFree > 1 ? 's' : ''} libre${seatsFree > 1 ? 's' : ''}`}` +
    ` sur ${seatsOffered} proposee${seatsOffered > 1 ? 's' : ''}`;

  const words = (
    <p className="seat-summary">
      <strong className={seatsFree > 0 ? 'ok' : 'full'}>{summary}</strong>
      <span className="faint">
        {' '}· voiture de {totalSeats} places, conducteur inclus
        {held > 0 && ` · ${held} gardee${held > 1 ? 's' : ''} pour le materiel`}
      </span>
    </p>
  );

  if (variant === 'summary') return words;

  /** An occupied seat: the token gives up the full name on hover or on tap. */
  const Named = ({ id, kind, name, role, label, seatColor }) => {
    if (detailed) {
      return (
        <span className="seat-static" title={`${name} — ${role}`}>
          <Seat kind={kind} name={name} label={label} color={seatColor} />
        </span>
      );
    }
    return (
      <button
        type="button"
        className="seat-button seat-named"
        data-open={openSeat === id ? 'true' : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpenSeat((cur) => (cur === id ? null : id));
        }}
        aria-label={`${name} — ${role}`}
        title={`${name} — ${role}`}
      >
        <Seat kind={kind} name={name} color={seatColor} />
        <span className="seat-tip" role="tooltip">{name}</span>
      </button>
    );
  };

  return (
    <div className={`seats ${detailed ? 'seats-detailed' : ''}`} ref={rootRef}>
      <div className="seat-row">
        <Named
          id="driver"
          kind="driver"
          name={driverName || 'Conducteur'}
          role="au volant"
          label={detailed ? firstName(driverName) : null}
          seatColor={color}
        />

        {passengers.slice(0, seatsTaken).map((p) => (
          <Named
            key={p.id}
            id={p.id}
            kind="taken"
            name={p.name}
            role="place occupee"
            label={detailed ? firstName(p.name) : null}
          />
        ))}
        {/* A ride form preview has counts but no people yet. */}
        {Array.from({ length: Math.max(0, seatsTaken - passengers.length) }, (_, i) => (
          <span key={`t${i}`} className="seat-static" title="Place occupee">
            <Seat kind="taken" name="?" label={detailed ? 'occupee' : null} />
          </span>
        ))}

        {Array.from({ length: seatsFree }, (_, i) =>
          onSeatClick ? (
            <button
              key={`f${i}`}
              type="button"
              className="seat-button seat-take"
              onClick={(e) => {
                e.stopPropagation();
                onSeatClick();
              }}
              aria-label="Prendre cette place"
              title="Prendre cette place"
            >
              <Seat kind="free" label={detailed ? 'libre' : null} />
              <span className="seat-tip">Prendre cette place</span>
            </button>
          ) : (
            <span key={`f${i}`} className="seat-static" title="Place libre">
              <Seat kind="free" label={detailed ? 'libre' : null} />
            </span>
          ),
        )}

        {detailed &&
          Array.from({ length: held }, (_, i) => (
            <span key={`h${i}`} className="seat-static" title="Siege non propose (materiel, encombrement...)">
              <Seat kind="held" label="gardee" />
            </span>
          ))}
      </div>

      {detailed ? (
        words
      ) : (
        <span className={`seat-count ${seatsFree > 0 ? '' : 'full'}`}>
          {seatsFree > 0 ? `${seatsFree} libre${seatsFree > 1 ? 's' : ''} / ${seatsOffered}` : 'complet'}
        </span>
      )}

      <span className="visually-hidden">
        {summary}. Voiture de {totalSeats} places conducteur inclus.
        {held > 0 && ` ${held} siege${held > 1 ? 's' : ''} non propose${held > 1 ? 's' : ''}.`}
      </span>
    </div>
  );
}
